import type { TOC } from '@ember/component/template-only';
import Text from './text';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for highlight cards.
 *
 * The 8-name decorative palette stays in the type for API back-compat
 * with existing callers, but each resolves to the TIO canonical 6-recipe
 * palette via the `.app-highlight-card--<name>` CSS class family. See
 * `addons/tio-ui/src/styles/`.
 */
export type HighlightCardColor =
  'emerald' | 'blue' | 'violet' | 'amber' | 'teal' | 'indigo' | 'pink' | 'rose';

interface HighlightCardSignature {
  Args: {
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Prominent value (e.g., "$5,250/year", "AI-Powered") */
    value: string;
    /** Description text */
    description?: string;
    /** Color theme for gradient and icon */
    color?: HighlightCardColor;
  };
  Blocks: {
    /** Optional extra content at the bottom */
    extra?: [];
  };
  Element: HTMLDivElement;
}

/**
 * HighlightCard - A gradient-background card for benefit/feature highlights.
 *
 * The card composes against two semantic class families:
 *   - `.app-highlight-card` for the card surface (gradient, border, padding,
 *     and color-keyed value text)
 *   - `.app-icon-badge` for the colored icon wrapper (REUSED — every card in
 *     the reimagined family already wraps its icon in this primitive instead
 *     of redefining its own padded, tinted icon-wrapper markup)
 *
 * @example
 * ```gts
 * import HighlightCard from 'tio-ui/components/highlight-card';
 *
 * <HighlightCard
 *   @icon={{PhGraduationCap}}
 *   @title="Tuition Assistance"
 *   @value="$5,250/year"
 *   @description="Tax-free education funding for degrees and certificates"
 *   @color="emerald"
 * />
 * ```
 */
const HighlightCard: TOC<HighlightCardSignature> = <template>
  {{#let (if @color @color "emerald") as |color|}}
    <div
      data-test-highlight-card
      class="app-highlight-card app-highlight-card--{{color}}"
      ...attributes
    >
      <div class="app-icon-badge app-icon-badge--{{color}} app-icon-badge--md mb-3 w-fit">
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} />
        {{/let}}
      </div>
      <div data-test-highlight-card-title class="app-highlight-card-title">
        {{@title}}
      </div>
      <div data-test-highlight-card-value class="app-highlight-card-value">
        {{@value}}
      </div>
      {{#if @description}}
        <Text data-test-highlight-card-description @variant="muted" class="text-sm">
          {{@description}}
        </Text>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-3">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default HighlightCard;

export type { HighlightCardSignature };
